'use client'

import React, { useState, createContext } from 'react'
import { ConfigProvider, theme as antdTheme } from 'antd'

type ClientConfig = {
  name: string
  logo: string
  darkLogo?: string
}

export type ThemeContextType = {
  isDarkMode: boolean
  setIsDarkMode: (isDark: boolean) => void
  toggleTheme: () => void
  handleClick: () => void
  currentClient: ClientConfig
  setCurrentClient: (client: ClientConfig) => void
  clients: ClientConfig[]
}

const defaultClients: ClientConfig[] = [
  {
    name: 'DemoClient',
    logo: '',
    darkLogo: '',
  },
  {
    name: 'PhysicianGPS',
    logo: '/assets/logo/PhysicianGPSLogoDark.png',
    darkLogo: '/assets/logo/PhysicianGPSLogoDark.png',
  },
  {
    name: 'Avalon.AI',
    logo: '/assets/logo/AvalonAILogo.png',
    darkLogo: '/assets/logo/AvalonAILogoDark.png',
  },
  { name: 'Takeda', logo: '/assets/logo/TakedaLogo.png' },
  { name: 'Healthcare365', logo: '/assets/logo/Healthcare365Logo.png' },
]

export const ThemeContext = createContext<ThemeContextType>({
  isDarkMode: false,
  setIsDarkMode: () => {},
  toggleTheme: () => {},
  handleClick: () => {},
  currentClient: defaultClients[0],
  setCurrentClient: () => {},
  clients: defaultClients,
})

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const { defaultAlgorithm, darkAlgorithm } = antdTheme
  const [isDarkMode, setIsDarkMode] = useState(false)
  const [currentClient, setCurrentClient] = useState(defaultClients[0])
  const [clients] = useState(defaultClients)

  const handleClick = () => {
    setIsDarkMode((prevValue) => !prevValue)
  }

  const config = {
    algorithm: isDarkMode ? darkAlgorithm : defaultAlgorithm,
    token: {
      colorPrimary: isDarkMode ? '#3CA4A6' : '#06686B',
      colorLink: isDarkMode ? '#28a5b0' : '#57ADB9',
      colorBgLayout: isDarkMode ? '#000000FF' : '#ffffff',
      fontSize: 16,
      colorTextSecondary: isDarkMode ? '#28a5b0' : '#57ADB9',
      colorTextTertiary: isDarkMode ? '#959599' : '#595959',
    },
  }

  return (
    <ThemeContext.Provider
      value={{
        isDarkMode,
        setIsDarkMode,
        toggleTheme: handleClick,
        handleClick,
        currentClient,
        setCurrentClient,
        clients,
      }}
    >
      <ConfigProvider theme={config}>{children}</ConfigProvider>
    </ThemeContext.Provider>
  )
}
